import { ReactNode, useCallback, useState } from 'react';
import { IconCustomAurora } from '@rossum/ui/icons/tabler';
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  inputClasses,
  outlinedInputClasses,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { usePlaceholderSwitcher } from './usePlaceholderSwitcher';

type CopilotDialogProps = {
  title: string;
  subtitle?: string;
  errorText: string;
  open: boolean;
  placeholders: string[];
  onSubmit: (value: string) => void;
  onClose: () => void;
  isLoading: boolean;
  isError: boolean;
  closeText: string;
  confirmText: string;
  summary?: ReactNode;
};

export const CopilotDialog = ({
  title,
  subtitle,
  errorText,
  open,
  placeholders,
  onSubmit,
  onClose,
  isLoading,
  isError,
  closeText,
  confirmText,
  summary,
}: CopilotDialogProps) => {
  const [value, setValue] = useState('');

  const { placeholderStyle, placeholder } = usePlaceholderSwitcher(
    placeholders,
    5000,
    value
  );

  const handleKeyDownInMultilineInput = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSubmit(value);
      }
    },
    [onSubmit, value]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: { position: 'relative', overflow: 'visible' },
      }}
      data-cy="copilot-dialog"
    >
      <Avatar
        sx={{
          bgcolor: t => t.palette.background.paper,
          zIndex: 1,
          position: 'absolute',
          right: 0,
          left: 0,
          margin: 'auto',
          top: '-32px',
          width: 64,
          height: 64,
        }}
      >
        <SvgIcon fontSize="xLarge" sx={{ color: t => t.palette.aurora.light }}>
          <IconCustomAurora />
        </SvgIcon>
      </Avatar>

      <DialogContent>
        <Stack
          spacing={2}
          pt={2}
          component="form"
          onSubmit={e => {
            onSubmit(value);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {summary}
          <Stack spacing={0.5} textAlign="center">
            <Typography variant="h5">{title}</Typography>

            {subtitle ? (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {subtitle}
              </Typography>
            ) : null}
          </Stack>

          <TextField
            onChange={e => setValue(e.currentTarget.value)}
            size="small"
            autoComplete="off"
            multiline
            maxRows={12}
            helperText={isError ? errorText : undefined}
            FormHelperTextProps={
              isError
                ? { sx: { color: t => t.palette.warning.main } }
                : undefined
            }
            autoFocus
            placeholder={placeholder}
            onKeyDown={handleKeyDownInMultilineInput}
            InputProps={{
              sx: {
                [`&.${inputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
                  {
                    borderColor: t => t.palette.aurora.light,
                  },
              },
              inputProps: {
                sx: {
                  '&::placeholder': placeholderStyle,
                },
                'data-cy': 'prompt-field',
              },
            }}
          />

          <Stack direction="row" alignSelf="center" spacing={1}>
            <Button
              color="inherit"
              data-cy="cancel-copilot-dialog-btn"
              onClick={onClose}
            >
              {closeText}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="aurora"
              disabled={isLoading}
              data-cy="submit-copilot-dialog-btn"
              startIcon={
                isLoading && <CircularProgress color="inherit" size={20} />
              }
            >
              {confirmText}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
